import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, Smartphone, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function InstallPWAButton({ variant = "default" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if user dismissed the banner
    const dismissed = localStorage.getItem('tireo_pwa_dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      // Show again after 7 days
      if ((now - dismissedDate) < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Listen for beforeinstallprompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if running on mobile and show banner after delay
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && !isInstalled) {
      setTimeout(() => setShowBanner(true), 3000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else {
      // iOS doesn't support beforeinstallprompt, show instructions
      alert(
        "Pour installer TIREO sur votre appareil :\n\n" +
        "📱 iPhone/iPad : Appuyez sur le bouton de partage, puis 'Sur l'écran d'accueil'\n\n" +
        "🤖 Android : Appuyez sur le menu (⋮), puis 'Installer l'application'"
      );
    }
  };

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('tireo_pwa_dismissed', new Date().toISOString());
  };

  if (isInstalled || !showBanner) return null;

  if (variant === "banner") {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-primary to-blue-600 shadow-lg"
        >
          <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">Installer TIREO</p>
                <p className="text-white/80 text-xs">Accès rapide et notifications</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleInstall}
                className="bg-white text-primary hover:bg-white/90"
              >
                <Download className="w-4 h-4 mr-1" />
                Installer
              </Button>
              <button
                onClick={dismissBanner}
                className="p-2 text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Compact button version
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleInstall}
      className="gap-2"
    >
      <Download className="w-4 h-4" />
      Installer l'app
    </Button>
  );
}
